import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { TPromoStoriesOption } from '../../types/options';
import { isMobileByTouch } from '../../utils/helpers';
import { getInlineStyles } from '../../utils/styles';
import { Progress } from './Progress';
import styles from './promoStories.module.css';

export type TStories = {
    isInverted?: boolean;
    duration?: number;
};

type TStoriesProps = TStories & {
    uid: string;
    widgetOptions: TPromoStoriesOption;
    autoplay?: boolean;
    infinityLoop?: boolean;
    steps: React.ReactNode[];
};

const Stories = ({ uid, widgetOptions, steps, infinityLoop, duration, isInverted, autoplay: _autoplay = true }: TStoriesProps) => {
    const [forward, setForward] = useState<boolean>(true);
    const [currentStepNumber, setCurrentStepNumber] = useState<number>(0);
    const [isPaused, setIsPaused] = useState(false);
    const [pressedDate, setPressedDate] = useState<number | null>(null);

    const overlayPressed = () => {
        setPressedDate(Date.now());
        setIsPaused(true);
    };

    const overlayReleased = (action: () => void) => {
        if (pressedDate !== null && Date.now() - pressedDate < 200) {
            action();
        }

        setPressedDate(null);
        setIsPaused(false);
    };

    const previousStep = useCallback(() => {
        setForward(false);
        setCurrentStepNumber((step) => {
            const isFirstStepIndex = step === 0;
            return isFirstStepIndex ? (infinityLoop ? steps.length - 1 : step) : step - 1;
        });
    }, [steps.length, infinityLoop]);

    const nextStep = useCallback(() => {
        setForward(true);
        setCurrentStepNumber((step) => {
            const isLastStepIndex = step === steps.length - 1;
            return isLastStepIndex ? (infinityLoop ? 0 : step) : step + 1;
        });
    }, [steps.length, infinityLoop]);

    const nextReleased = () => overlayReleased(nextStep);
    const previousReleased = () => overlayReleased(previousStep);

    return (
        <div data-test={uid} className={styles.container} style={getInlineStyles({ margin: widgetOptions.margin })}>
            {steps.length > 1 && (
                <>
                    <Progress
                        key={currentStepNumber}
                        count={steps.length}
                        isPaused={isPaused}
                        currentStep={currentStepNumber}
                        onStepComplete={nextStep}
                        duration={duration}
                        isInverted={isInverted}
                    />
                    <div className={styles.overlay}>
                        <div
                            onTouchStart={overlayPressed}
                            onTouchEnd={previousReleased}
                            onMouseDown={() => {
                                if (!isMobileByTouch()) {
                                    overlayPressed();
                                }
                            }}
                            onMouseUp={() => {
                                if (!isMobileByTouch()) {
                                    previousReleased();
                                }
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                        <div
                            onTouchStart={overlayPressed}
                            onTouchEnd={nextReleased}
                            onMouseDown={() => {
                                if (!isMobileByTouch()) {
                                    overlayPressed();
                                }
                            }}
                            onMouseUp={() => {
                                if (!isMobileByTouch()) {
                                    nextReleased();
                                }
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </div>
                </>
            )}
            <div className={clsx(styles.storiesContent, forward ? styles.forward : styles.backwards)}>{steps[currentStepNumber]}</div>
        </div>
    );
};
export { Stories };
